'use client';

import { ChevronDown, ExternalLink, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Skeleton,
} from '@/components/ui';
import type {
  Attempt,
  Epic,
  Repository,
  Task,
  TaskReadoutState,
  TaskStateFilter,
  TaskStatusResponse,
} from '@/lib/admin/client';
import { isAttemptTransitionConflict } from '@/lib/admin/client';
import {
  deriveTaskReadouts,
  taskAttemptsByID,
  taskReadoutLabel,
  taskReadoutRank,
} from '@/lib/admin/taskReadout';
import { useAttemptTransition } from '@/lib/query/useAttemptTransition';
import { useTaskStatus } from '@/lib/query/useTaskStatus';

import { AttemptActivityDrilldown } from './AttemptActivityDrilldown';
import { AttemptTransitionControls } from './AttemptTransitionControls';
import {
  attemptBadgeVariant,
  priorityBadgeVariant,
  subtleStatusBadgeBackground,
  taskReadoutStateBadgeVariant,
} from './badgeVariants';
import { PanelTitle } from './PanelTitle';

const ACTIVE_ATTEMPT_STATES = new Set(['leased', 'in_progress', 'submitted']);
const TASK_SORT = {
  OPERATIONAL_PRIORITY: 'operational_priority',
  TASK_PRIORITY: 'task_priority',
  RECENTLY_UPDATED: 'recently_updated',
  TITLE: 'title',
} as const;
type TaskSort = (typeof TASK_SORT)[keyof typeof TASK_SORT];
const TASK_SCOPE_VALUE = {
  ALL: '',
  REPOSITORY_PREFIX: 'repository:',
  WORKTREE_PREFIX: 'worktreePath:',
} as const;
const TASK_STATE_OPTIONS: Array<{ label: string; value: TaskStateFilter }> = [
  { label: 'All states', value: 'all' },
  { label: 'Running', value: 'running' },
  { label: 'Retrying', value: 'retrying' },
  { label: 'Ready', value: 'ready' },
  { label: 'Waiting', value: 'waiting' },
  { label: 'Blocked', value: 'blocked' },
  { label: 'Done', value: 'done' },
  { label: 'Delivered', value: 'delivered' },
];
const EXECUTION_SECTION_LABEL = {
  OBJECTIVE: 'Objective',
  'ACCEPTANCE CRITERIA': 'Acceptance criteria',
  VERIFICATION: 'Verification',
  'DONE WHEN': 'Done when',
  DEPENDENCIES: 'Dependencies',
} as const;
type ExecutionSectionLabel = keyof typeof EXECUTION_SECTION_LABEL;
const EXECUTION_SECTION_PATTERN =
  /(?:^|\s)(OBJECTIVE|ACCEPTANCE CRITERIA|VERIFICATION|DONE WHEN|DEPENDENCIES):\s*/g;

type Rollup = {
  project: Repository;
  epics: Array<{
    plan: Epic;
    tasks: Array<{
      task: Task;
      attempts: Attempt[];
      state: TaskReadoutState;
      queueRank: number;
    }>;
  }>;
};

function latestAttempt(attempts: Attempt[]): Attempt | undefined {
  return attempts.at(-1);
}

function executionSections(
  goal: string
): Array<{ label: string; body: string }> | null {
  const matches = Array.from(goal.matchAll(EXECUTION_SECTION_PATTERN));
  if (matches.length === 0 || matches[0].index !== 0) return null;
  return matches.map((match, index) => ({
    label: EXECUTION_SECTION_LABEL[match[1] as ExecutionSectionLabel],
    body: goal
      .slice(
        (match.index ?? 0) + match[0].length,
        matches[index + 1]?.index ?? goal.length
      )
      .trim(),
  }));
}

function matchesState(
  state: TaskReadoutState,
  stateFilter: TaskStateFilter
): boolean {
  if (stateFilter === 'all') return true;
  return state === stateFilter;
}

function buildRollups(
  data: TaskStatusResponse,
  search: string,
  stateFilter: TaskStateFilter,
  sort: TaskSort,
  worktreeFilter: string | null,
  assigneeFilter: string
): Rollup[] {
  const attemptsByTask = taskAttemptsByID(data.attempts);
  const taskReadouts = deriveTaskReadouts(data);

  const tasksByPlan = new Map<string, Task[]>();
  for (const task of data.tasks) {
    const tasks = tasksByPlan.get(task.epicId) ?? [];
    tasks.push(task);
    tasksByPlan.set(task.epicId, tasks);
  }

  const plansByProject = new Map<string, Epic[]>();
  for (const plan of data.epics) {
    const epics = plansByProject.get(plan.repositoryId) ?? [];
    epics.push(plan);
    plansByProject.set(plan.repositoryId, epics);
  }

  const normalizedSearch = search.trim().toLowerCase();
  return data.repositories.flatMap((project) => {
    const epics = (plansByProject.get(project.repositoryId) ?? []).flatMap(
      (plan) => {
        const tasks = (tasksByPlan.get(plan.epicId) ?? []).flatMap((task) => {
          const attempts = attemptsByTask.get(task.taskId) ?? [];
          const readout = taskReadouts.get(task.taskId);
          if (!readout) return [];
          const state = readout.state;
          const searchable = [
            task.taskId,
            task.title,
            plan.goal,
            ...attempts.flatMap((attempt) => [attempt.conclusionReason]),
          ]
            .join(' ')
            .toLowerCase();
          if (!matchesState(state, stateFilter)) return [];
          if (
            worktreeFilter !== null &&
            !attempts.some((attempt) => attempt.worktreePath === worktreeFilter)
          ) {
            return [];
          }
          if (
            assigneeFilter !== '' &&
            !attempts.some((attempt) => attempt.workerId === assigneeFilter)
          ) {
            return [];
          }
          if (normalizedSearch && !searchable.includes(normalizedSearch)) {
            return [];
          }
          return [{ task, attempts, state, queueRank: readout.queueRank }];
        });
        tasks.sort((left, right) => {
          if (sort === TASK_SORT.TASK_PRIORITY) {
            return (
              left.task.priority - right.task.priority ||
              left.task.taskId.localeCompare(right.task.taskId)
            );
          }
          if (sort === TASK_SORT.RECENTLY_UPDATED) {
            const leftUpdated =
              latestAttempt(left.attempts)?.updatedAt ?? left.task.createdAt;
            const rightUpdated =
              latestAttempt(right.attempts)?.updatedAt ?? right.task.createdAt;
            return (
              rightUpdated.localeCompare(leftUpdated) ||
              left.task.taskId.localeCompare(right.task.taskId)
            );
          }
          if (sort === TASK_SORT.TITLE) {
            return (
              left.task.title.localeCompare(right.task.title) ||
              left.task.taskId.localeCompare(right.task.taskId)
            );
          }
          return (
            taskReadoutRank(left.state) - taskReadoutRank(right.state) ||
            left.queueRank - right.queueRank ||
            left.task.priority - right.task.priority ||
            left.task.taskId.localeCompare(right.task.taskId)
          );
        });
        return tasks.length > 0 ? [{ plan, tasks }] : [];
      }
    );
    return epics.length > 0 ? [{ project, epics }] : [];
  });
}

export type TaskStatusPanelProps = {
  overviewData?: TaskStatusResponse | null;
  stateFilter?: TaskStateFilter;
  onStateFilterChange?: (filter: TaskStateFilter) => void;
};

export function TaskStatusPanel({
  overviewData,
  stateFilter: controlledStateFilter,
  onStateFilterChange,
}: TaskStatusPanelProps = {}) {
  const [scopeValue, setScopeValue] = useState<string>(TASK_SCOPE_VALUE.ALL);
  const [search, setSearch] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [sort, setSort] = useState<TaskSort>(TASK_SORT.OPERATIONAL_PRIORITY);
  const [uncontrolledStateFilter, setUncontrolledStateFilter] =
    useState<TaskStateFilter>('all');
  const isStateControlled =
    controlledStateFilter !== undefined && onStateFilterChange !== undefined;
  const stateFilter = isStateControlled
    ? controlledStateFilter
    : uncontrolledStateFilter;
  const [expandedAttemptByTask, setExpandedAttemptByTask] = useState<
    Record<string, string | null>
  >({});
  const catalog = useTaskStatus(null);
  const catalogData =
    overviewData ??
    (catalog.data?.status === 'available' ? catalog.data.data : null);
  const scopeGroups = useMemo(
    () =>
      (catalogData?.repositories ?? [])
        .map((project) => ({
          project,
          repositoryValue: `${TASK_SCOPE_VALUE.REPOSITORY_PREFIX}${project.repositoryId}`,
          worktrees: Array.from(
            new Set(
              (catalogData?.attempts ?? [])
                .filter(
                  (attempt) =>
                    attempt.repositoryId === project.repositoryId &&
                    attempt.worktreePath !== ''
                )
                .map((attempt) => attempt.worktreePath)
            )
          )
            .sort()
            .map((worktreePath) => ({
              worktreePath,
              value: `${TASK_SCOPE_VALUE.WORKTREE_PREFIX}${project.repositoryId}:${encodeURIComponent(worktreePath)}`,
            })),
        }))
        .sort((left, right) =>
          left.project.originUrl.localeCompare(right.project.originUrl)
        ),
    [catalogData]
  );
  const selectedScope = scopeGroups
    .flatMap((group) => [
      {
        value: group.repositoryValue,
        originUrl: group.project.originUrl,
        worktreePath: null,
      },
      ...group.worktrees.map((worktreePath) => ({
        value: worktreePath.value,
        originUrl: group.project.originUrl,
        worktreePath: worktreePath.worktreePath,
      })),
    ])
    .find((scope) => scope.value === scopeValue);
  const originUrl = selectedScope?.originUrl ?? null;
  const worktreeFilter = selectedScope?.worktreePath ?? null;
  const assignees = Array.from(
    new Set(
      (catalogData?.attempts ?? [])
        .map((attempt) => attempt.workerId)
        .filter((workerId) => workerId !== '')
    )
  ).sort();
  const status = useTaskStatus(originUrl);
  const transition = useAttemptTransition();
  const statusData =
    originUrl === null && overviewData !== undefined
      ? overviewData
      : status.data?.status === 'available'
        ? status.data.data
        : null;
  const isDisabled = status.data?.status === 'disabled';
  const rollups = useMemo(
    () =>
      statusData
        ? buildRollups(
            statusData,
            search,
            stateFilter,
            sort,
            worktreeFilter,
            assigneeFilter
          )
        : ([] as Rollup[]),
    [assigneeFilter, search, sort, stateFilter, statusData, worktreeFilter]
  );
  const visibleTaskCount = rollups.reduce(
    (count, rollup) =>
      count +
      rollup.epics.reduce(
        (planCount, planRollup) => planCount + planRollup.tasks.length,
        0
      ),
    0
  );
  const tasksByID = new Map(
    (statusData?.tasks ?? []).map((task) => [task.taskId, task])
  );
  const taskReadouts = statusData ? deriveTaskReadouts(statusData) : new Map();
  const attentionAttempts = Array.from(
    (statusData?.attempts ?? [])
      .reduce((byTask, attempt) => {
        const previous = byTask.get(attempt.taskId);
        if (
          !previous ||
          previous.createdAt.localeCompare(attempt.createdAt) < 0
        ) {
          byTask.set(attempt.taskId, attempt);
        }
        return byTask;
      }, new Map<string, Attempt>())
      .values()
  ).filter((attempt) => taskReadouts.get(attempt.taskId)?.state === 'blocked');
  const transitionConflict = isAttemptTransitionConflict(transition.error);
  const transitionFailed = transition.isError && !transitionConflict;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PanelTitle>Tasks</PanelTitle>
          <Badge variant={isDisabled ? 'outline' : 'secondary'}>
            {isDisabled ? 'Disabled' : `${visibleTaskCount} shown`}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 pt-4">
        {attentionAttempts.length > 0 && (
          <section
            aria-label="Needs attention"
            className="border-destructive/30 space-y-2 rounded-md border p-3"
          >
            <div className="flex items-center gap-2">
              <h3 className="font-medium">Needs attention</h3>
              <Badge
                data-attention-count
                variant="error"
                className={subtleStatusBadgeBackground('error')}
              >
                {attentionAttempts.length}
              </Badge>
            </div>
            <div className="grid gap-2 lg:grid-cols-2">
              {attentionAttempts.map((attempt) => (
                <a
                  key={attempt.attemptId}
                  href={`#task-${attempt.taskId}`}
                  className="border-destructive/30 focus-visible:ring-ring flex min-w-0 items-center justify-between gap-3 rounded-md border bg-[color-mix(in_oklab,var(--destructive)_5%,transparent)] px-3 py-2 text-left transition-colors outline-none hover:bg-[color-mix(in_oklab,var(--destructive)_10%,transparent)] focus-visible:ring-2"
                  onClick={() =>
                    setExpandedAttemptByTask((current) => ({
                      ...current,
                      [attempt.taskId]: attempt.attemptId,
                    }))
                  }
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {tasksByID.get(attempt.taskId)?.title ?? attempt.taskId}
                    </span>
                    <span className="text-muted-foreground block truncate text-xs">
                      {attempt.conclusionReason ||
                        'Review the latest Attempt evidence.'}
                    </span>
                  </span>
                  <Badge
                    data-attention-state-badge
                    data-status-tone={taskReadoutStateBadgeVariant('blocked')}
                    variant={taskReadoutStateBadgeVariant('blocked')}
                    className={subtleStatusBadgeBackground(
                      taskReadoutStateBadgeVariant('blocked')
                    )}
                  >
                    Blocked
                  </Badge>
                </a>
              ))}
            </div>
          </section>
        )}
        {!isDisabled && (
          <div
            data-task-filter-grid
            className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[minmax(16rem,1.25fr)_minmax(12rem,1fr)_minmax(10rem,0.75fr)_10rem_12rem_auto]"
          >
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium uppercase">
                Repository / worktreePath
              </span>
              <select
                aria-label="Scope by repository or worktreePath"
                value={scopeValue}
                onChange={(event) => {
                  setScopeValue(event.target.value);
                  setExpandedAttemptByTask({});
                }}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value={TASK_SCOPE_VALUE.ALL}>All repositories</option>
                {scopeGroups.map((group) => (
                  <optgroup
                    key={group.project.repositoryId}
                    label={group.project.originUrl}
                  >
                    <option value={group.repositoryValue}>All worktrees</option>
                    {group.worktrees.map((worktreePath) => (
                      <option
                        key={worktreePath.value}
                        value={worktreePath.value}
                      >
                        {worktreePath.worktreePath}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium uppercase">
                Tasks
              </span>
              <Input
                type="search"
                aria-label="Filter tasks"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Task, objective, or conclusion reason"
                className="bg-background dark:bg-background"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium uppercase">
                Assignee
              </span>
              <select
                aria-label="Filter by workerId"
                value={assigneeFilter}
                onChange={(event) => {
                  setAssigneeFilter(event.target.value);
                  setExpandedAttemptByTask({});
                }}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="">All assignees</option>
                {assignees.map((workerId) => (
                  <option key={workerId} value={workerId}>
                    {workerId}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium uppercase">
                State
              </span>
              <select
                aria-label="Filter by task state"
                value={stateFilter}
                onChange={(event) => {
                  const nextFilter = event.target.value as TaskStateFilter;
                  if (isStateControlled) {
                    onStateFilterChange(nextFilter);
                  } else {
                    setUncontrolledStateFilter(nextFilter);
                  }
                  setExpandedAttemptByTask({});
                }}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                {TASK_STATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium uppercase">
                Sort
              </span>
              <select
                aria-label="Sort tasks"
                value={sort}
                onChange={(event) => setSort(event.target.value as TaskSort)}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value={TASK_SORT.OPERATIONAL_PRIORITY}>Urgency</option>
                <option value={TASK_SORT.TASK_PRIORITY}>Task priority</option>
                <option value={TASK_SORT.RECENTLY_UPDATED}>
                  Recently updated
                </option>
                <option value={TASK_SORT.TITLE}>Title</option>
              </select>
            </label>
            <div className="flex items-end">
              <Button
                type="button"
                variant="outline"
                aria-label="Refresh task status"
                onClick={() => status.refetch()}
                disabled={status.isFetching}
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}

        {transitionConflict && (
          <div
            role="alert"
            className="border-border bg-muted flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
          >
            <span>
              This attempt changed or became terminal. Task status has been
              refreshed.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => transition.reset()}
            >
              Dismiss
            </Button>
          </div>
        )}
        {transitionFailed && (
          <p className="text-destructive text-sm">
            The attempt transition could not be saved. Please try again.
          </p>
        )}

        {status.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {status.isError && (
          <p className="text-destructive text-sm">
            Task status could not be loaded.
          </p>
        )}
        {isDisabled && (
          <p className="text-muted-foreground text-sm">
            MIND_TASK_SYSTEM_ENABLED is off for this backend, so the task status
            route is not mounted.
          </p>
        )}
        {!status.isLoading && !status.isError && !isDisabled && !statusData && (
          <p className="text-muted-foreground text-sm">
            Task status is unavailable.
          </p>
        )}
        {statusData && rollups.length === 0 && (
          <p className="text-muted-foreground text-sm">
            No tasks match this scope and filter.
          </p>
        )}

        {rollups.map(({ project, epics }) => (
          <section
            key={project.repositoryId}
            data-project-id={project.repositoryId}
            className="border-border space-y-4 rounded-lg border p-4"
          >
            <div className="mb-0 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold break-all">
                {project.originUrl}
              </h3>
              <Badge variant="outline">{project.status}</Badge>
            </div>
            {epics.map(({ plan, tasks }) => (
              <section
                key={plan.epicId}
                data-plan-id={plan.epicId}
                className="space-y-3 rounded-md py-3"
              >
                <div className="space-y-3">
                  {tasks.map(({ task, attempts, state }) => {
                    const currentAttempt = latestAttempt(attempts);
                    const objectiveSections = executionSections(plan.goal);
                    const configuredExpandedAttemptId =
                      expandedAttemptByTask[task.taskId];
                    const expandedAttemptId =
                      configuredExpandedAttemptId === undefined
                        ? currentAttempt && state === 'blocked'
                          ? currentAttempt.attemptId
                          : null
                        : configuredExpandedAttemptId;
                    return (
                      <article
                        key={task.taskId}
                        id={`task-${task.taskId}`}
                        data-task-id={task.taskId}
                        data-state={state}
                        className="bg-background scroll-mt-24 space-y-3 rounded-md p-3"
                      >
                        <div
                          data-task-header
                          className="mb-0 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3"
                        >
                          <div className="min-w-0 space-y-1">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                              <Badge
                                variant={priorityBadgeVariant(
                                  `P${task.priority}`
                                )}
                              >
                                P{task.priority}
                              </Badge>
                              <code className="text-muted-foreground text-xs">
                                {task.taskId}
                              </code>
                            </div>
                            <p className="px-3 font-medium">{task.title}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge
                              data-task-state-badge
                              data-status-tone={taskReadoutStateBadgeVariant(
                                state
                              )}
                              variant={taskReadoutStateBadgeVariant(state)}
                              className={subtleStatusBadgeBackground(
                                taskReadoutStateBadgeVariant(state)
                              )}
                            >
                              {taskReadoutLabel(state)}
                            </Badge>
                            {currentAttempt &&
                              ACTIVE_ATTEMPT_STATES.has(
                                currentAttempt.state
                              ) && <Badge variant="info">Lease active</Badge>}
                          </div>
                        </div>
                        {objectiveSections === null ? (
                          <p
                            data-task-execution-objective
                            className="text-foreground px-3 py-2 text-sm"
                          >
                            {plan.goal}
                          </p>
                        ) : (
                          <div
                            data-task-execution-objective
                            className="text-foreground space-y-2 px-3 py-2 text-sm"
                          >
                            {objectiveSections.map((section) => (
                              <p
                                key={section.label}
                                data-task-execution-section
                                className="space-y-1"
                              >
                                <strong className="text-muted-foreground block text-xs font-medium uppercase">
                                  {section.label}
                                </strong>
                                <span className="text-foreground block font-medium">
                                  {section.body}
                                </span>
                              </p>
                            ))}
                          </div>
                        )}
                        {attempts.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {attempts.map((attempt) => (
                              <div
                                key={attempt.attemptId}
                                data-attempt-id={attempt.attemptId}
                                data-attempt-state={attempt.state}
                                className="border-border w-full rounded-md border px-2 py-1"
                              >
                                <div
                                  className={`flex flex-wrap items-center gap-2 ${
                                    expandedAttemptId === attempt.attemptId
                                      ? 'mb-4'
                                      : ''
                                  }`}
                                >
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-expanded={
                                      expandedAttemptId === attempt.attemptId
                                    }
                                    aria-controls={`attempt-${attempt.attemptId}`}
                                    aria-label={`${
                                      expandedAttemptId === attempt.attemptId
                                        ? 'Collapse'
                                        : 'Expand'
                                    } attempt ${attempt.attemptId}`}
                                    onClick={() =>
                                      setExpandedAttemptByTask((current) => ({
                                        ...current,
                                        [task.taskId]:
                                          expandedAttemptId ===
                                          attempt.attemptId
                                            ? null
                                            : attempt.attemptId,
                                      }))
                                    }
                                  >
                                    <ChevronDown
                                      className={`h-4 w-4 transition-transform ${
                                        expandedAttemptId === attempt.attemptId
                                          ? 'rotate-180'
                                          : ''
                                      }`}
                                      aria-hidden="true"
                                    />
                                    <span>Attempt {attempt.iteration + 1}</span>
                                    <span aria-hidden="true">·</span>
                                    <code className="text-muted-foreground text-xs break-all">
                                      {attempt.attemptId}
                                    </code>
                                  </Button>
                                  {attempt.prUrl && (
                                    <a
                                      href={attempt.prUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      aria-label={`Open pull request for ${attempt.attemptId}`}
                                      className="text-sm underline"
                                    >
                                      PR
                                      <ExternalLink
                                        className="ml-1 inline h-3 w-3"
                                        aria-hidden="true"
                                      />
                                    </a>
                                  )}
                                  <AttemptTransitionControls
                                    attempt={attempt}
                                    isPending={transition.isPending}
                                    onTransition={(attemptId, nextTransition) =>
                                      transition.mutate({
                                        attemptId,
                                        transition: nextTransition,
                                      })
                                    }
                                  />
                                  <Badge
                                    data-attempt-state-badge
                                    data-status-tone={attemptBadgeVariant(
                                      attempt
                                    )}
                                    variant={attemptBadgeVariant(attempt)}
                                    className={`ml-auto ${subtleStatusBadgeBackground(
                                      attemptBadgeVariant(attempt)
                                    )}`}
                                  >
                                    {attempt.conclusion
                                      ? `${attempt.state} · ${attempt.conclusion.replaceAll('_', ' ')}`
                                      : attempt.state.replaceAll('_', ' ')}
                                  </Badge>
                                </div>
                                {expandedAttemptId === attempt.attemptId && (
                                  <div
                                    id={`attempt-${attempt.attemptId}`}
                                    data-testid="task-attempt-trace"
                                    className="scroll-mt-24 px-2 pb-2"
                                  >
                                    <AttemptActivityDrilldown
                                      attempt={attempt}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </section>
        ))}
      </CardContent>
    </Card>
  );
}
